import {
  defaultNotificationSettings,
  silentThunkResponse,
  thunkResponse,
} from "../app/api";
import { getCurrentUser } from "../app/gs-session";
import { getErrors, longDelay, mediumDelay } from "../helpers/JSONapi";
import {
  deleteOrderFromLocalStorage,
  getCustomerFromLocalStorage,
  getOrderFromLocalStorage,
  RegistrationOrder,
  saveCustomerToLocalStorage,
  updateRegistrationOrder,
} from "../models/Registration";
import {
  addDonationToOrderThunk,
  addTournamentPackageToOrderThunk,
  applyDiscountThunk,
  calculatePricing,
  createAndPayThunk,
  createBillingAddressThunk,
  deleteOrderThunk,
  deleteTournamentRegistrationOrderItemThunk,
  getRegistrationOrderThunk,
  internationalCardCheckThunk,
  payOrderThunk,
  removeDonationOrderItemThunk,
  removeTournamentPackageFromOrderThunk,
  saveCustomerThunk,
  updateDonationItemThunk,
  updateInProgressPlayerThunk,
  updateInProgressSponsorThunk,
  updateInProgressTeamThunk,
  updateOrderThunk,
} from "../reducers/registrationOrderSlice";
import {
  getPublicTournamentDetails,
  getTournamentAccessCode,
} from "../reducers/tournamentSlice";
import { faCartPlus } from "@fortawesome/free-solid-svg-icons";

export const savingOrderNotifications = {
  pending: {
    ...defaultNotificationSettings.pending,
    message: () => "Saving your order...",
  },
  fulfilled: {
    ...defaultNotificationSettings.fulfilled,
    message: () => {
      return "Your order has been saved.";
    },
  },
  rejected: {
    ...defaultNotificationSettings.rejected,
    message: (state, action) => {
      return getErrors(action, "Could not save order.");
    },
  },
};

export const getPublicTournamentDetailsResponse = (builder) =>
  silentThunkResponse(builder, getPublicTournamentDetails, (state, action) => {
    state.registrationCustomer.tournament = action.payload;
    state.tournament = action.payload;
    if (
      state.registrationOrder === null ||
      state?.registrationOrder?.tourament?.id !== action?.payload?.id
    ) {
      const localOrder = getOrderFromLocalStorage(action.payload);
      state.registrationOrder = localOrder;
    }
    const customer = getCustomerFromLocalStorage(action?.payload?.id);
    updateLocalCustomer(state, customer, action.payload);
  });

export const updateRemoteRegistrationOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    updateOrderThunk,
    (state, action) => {
      const { paymentOption, card, saveCard } = action?.meta?.arg;
      state.registrationOrder = {
        ...state.registrationOrder,
        ...action?.payload,
        paymentOption,
        card,
        saveCard,
      };
    },
    savingOrderNotifications
  );

export const deleteRemoteRegistrationOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    deleteOrderThunk,
    (state, action) => {
      deleteOrderFromLocalStorage(action?.meta?.arg);
      updateRegistrationOrder(state, {
        ...RegistrationOrder,
        tournament: state.tournament,
      });
    },
    savingOrderNotifications
  );

export const getRegistrationOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    getRegistrationOrderThunk,
    (state, action) => {
      const currentOrder = { ...state.registrationOrder, ...action.payload };
      if (!currentOrder.card) {
        currentOrder.globalPaymentToken = null;
        currentOrder.stripePaymentToken = null;
      }
      if (!getCurrentUser()) {
        currentOrder.paymentOption = null;
      }

      updateRegistrationOrder(state, currentOrder);
      if (action?.payload?.registrationCustomer?.id !== "blank_customer") {
        updateLocalCustomer(
          state,
          action.payload?.registrationCustomer,
          state.tournament
        );
      }
    },
    {
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          updateRegistrationOrder(state, {
            ...RegistrationOrder,
            tournament: state.tournament,
          });
          return "You do not have access to this order.";
        },
      },
    }
  );

export const addTournamentPackageToOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    addTournamentPackageToOrderThunk,
    (state, action) => {
      const currentOrder = { ...state.registrationOrder, ...action.payload };
      updateRegistrationOrder(state, currentOrder);
    },
    {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Adding Package to Order...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        icon: faCartPlus,
        message: () => "Package has been added to your cart",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        timeout: mediumDelay,
        message: (state, action) => {
          return getErrors(action, "Could not save order");
        },
      },
    }
  );

export const payOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    payOrderThunk,
    (state, action) => {
      state.completedOrder = { ...state.registrationOrder };
      updateRegistrationOrder(state, {
        ...RegistrationOrder,
        tournament: state.tournament,
      });
      const {
        firstName,
        lastName,
        email,
        phone,
        tournament,
      } = state.registrationCustomer;
      updateLocalCustomer(
        state,
        {
          firstName,
          lastName,
          email,
          phone,
        },
        tournament
      );
    },
    {
      ...defaultNotificationSettings,
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Processing...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Payment was processed!",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        timeout: longDelay,
        message: (state, action) => {
          return getErrors(action, "Please double-check your payment details.");
        },
        description: () => "We couldn't process your payment.",
      },
    }
  );

export const removeTournamentPackageFromOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    removeTournamentPackageFromOrderThunk,
    (state, action) => {
      const orderItem = action.payload;
      const items = [...state?.registrationOrder?.registrationOrderItems];
      const itemIndex = items.findIndex?.((i) => i.id === orderItem.id);
      if (itemIndex >= 0) {
        items[itemIndex] = action.payload;
        const currentOrder = {
          ...state.registrationOrder,
          ...orderItem.registrationOrder,
          registrationOrderItems: items,
        };
        updateRegistrationOrder(state, currentOrder);
      }
    },
    {
      ...defaultNotificationSettings,
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Removing Package from Order...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Package has been removed to your cart",
      },
    }
  );

export const deleteTournamentRegistrationOrderItemThunkresponse = (builder) =>
  thunkResponse(
    builder,
    deleteTournamentRegistrationOrderItemThunk,
    (state, action) => {
      const order = { ...state.registrationOrder, ...action.payload };
      updateRegistrationOrder(state, order);
    },
    {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Removing package from your order...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Package has been removed from your order.",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          return getErrors(action, "Unable to remove package from your order.");
        },
      },
    }
  );

export const saveCustomerThunkResponse = (builder) =>
  thunkResponse(
    builder,
    saveCustomerThunk,
    (state, action) => {
      updateLocalCustomer(state, action.payload, action?.meta?.arg?.tournament);
    },
    savingOrderNotifications
  );

export const updateLocalCustomer = (state, customer, tournament) => {
  const update = {
    ...customer,
    tournament: tournament,
  };
  state.registrationOrder = {
    ...state.registrationOrder,
    registrationCustomer: customer,
  };
  state.registrationCustomer = update;
  saveCustomerToLocalStorage(update);
};

export const addDonationToOrderThunkResponse = (builder) =>
  thunkResponse(
    builder,
    addDonationToOrderThunk,
    (state, action) => {
      const donation = action.payload;
      const order = { ...state.registrationOrder, ...donation };
      updateRegistrationOrder(state, order);
    },
    savingOrderNotifications
  );

export const updateDonationOrderItemThunkResponse = (builder) =>
  thunkResponse(
    builder,
    updateDonationItemThunk,
    (state, action) => {
      const donation = action.payload?.registrationOrder;
      const order = { ...state.registrationOrder, ...donation };
      updateRegistrationOrder(state, order);
    },
    savingOrderNotifications
  );

export const removeDonationOrderItemThunkResponse = (builder) =>
  thunkResponse(
    builder,
    removeDonationOrderItemThunk,
    (state, action) => {
      updateRegistrationOrder(state, {
        ...state.registrationOrder,
        ...action.payload,
      });
    },
    {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Removing donation from order...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Donation has been removed to your order.",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          return getErrors(action, "We could not remove your donation at this time.");
        },
      },
    }
  );

//old api usage

export const calculatePricingRespose = (builder) =>
  thunkResponse(
    builder,
    calculatePricing,
    (state, action) => {
      const update = {
        ...state.registrationOrder,
        ...action.payload,
      };
      updateRegistrationOrder(state, update);
    },
    {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Calculating price...",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: () => {
          return "Please try again";
        },
        description: () => {
          return "Could not calculate the final price.";
        },
      },
    }
  );

export const createAndPayThunkResponse = (builder) =>
  thunkResponse(
    builder,
    createAndPayThunk,
    (state) => {
      state.completedOrder = { ...state.registrationOrder };
      const {
        accessIdentifier,
        accessValidated,
        accessEmail,
        accessGroupID,
        accessGroupMemberID,
      } = state.registrationOrder;
      updateRegistrationOrder(state, {
        ...RegistrationOrder,
        accessIdentifier,
        accessEmail,
        accessValidated,
        accessGroupID,
        accessGroupMemberID,
        tournament: state.tournament,
      });
    },
    {
      ...defaultNotificationSettings,
      pending: {
        ...defaultNotificationSettings.pending,
        message: () =>
          "We’re processing your order, please don’t refresh this page.",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        timeout: longDelay,
        message: (state, action) => {
          return (
            action?.payload?.message ??
            "Please double-check your payment details."
          );
        },
        description: () => "We couldn't process your payment.",
      },
    }
  );

export const applyDiscountThunkResponse = (builder) =>
  thunkResponse(
    builder,
    applyDiscountThunk,
    (state, action) => {
      const orderUpdate = {
        ...state.registrationOrder,
        ...action.payload,
      };
      updateRegistrationOrder(state, orderUpdate);
    },
    {
      pending: false,
      fulfilled: false,
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          return getErrors(action, "Not a valid discount code.");
        },
        timeout: mediumDelay,
      },
    }
  );

export const internationalCardCheckThunkResponse = (builder) =>
  thunkResponse(
    builder,
    internationalCardCheckThunk,
    (state, action) => {
      const order = { ...state.registrationOrder, ...action.payload };
      updateRegistrationOrder(state, order);
    },
    {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Saving Payment Information...",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          return (
            action?.payload?.message ??
            "Please double-check your payment details."
          );
        },
        description: () => "We couldn't process your payment.",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Payment has been saved to order.",
      },
    }
  );

export const getAccessCodeThunkResponse = (builder) =>
  thunkResponse(
    builder,
    getTournamentAccessCode,
    (state, action) => {
      state.tournamentAccessCodes = action.payload;
      if (action.payload?.length > 0) {
        const access = action.payload[0];
        const order = {
          ...state.registrationOrder,
          accessIdentifier: access?.identifier?.toLowerCase?.(),
          accessValidated: true,
          accessEmail: access?.credentials?.accessEmail,
          accessGroupID: access?.accessGroup?.id,
          accessGroupMemberID: access?.id,
        };
        updateRegistrationOrder(state, order);
      }
    },
    {
      ...defaultNotificationSettings,
      rejected: {
        ...defaultNotificationSettings.rejected,
        message: (state, action) => {
          return getErrors(action, "This email address and access code combination is incorrect.");
        },
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "Your credentials have been validated!",
      },
    }
  );

export const createBillingAddressThunkResponse = (builder) => {
  thunkResponse(builder, createBillingAddressThunk, () => {
  }, {
    rejected: {
      ...defaultNotificationSettings.rejected,
      message: (state, action) => {
        return getErrors(action, "We were unable to save your address. Please update and try again.");
      },
    },
  });
};

export const updateInProgressTeamThunkResponse = (builder) =>
  thunkResponse(builder, updateInProgressTeamThunk, () => {}, {
    pending: {
      ...defaultNotificationSettings.pending,
      message: () => "Saving your order",
    },
    rejected: {
      ...defaultNotificationSettings.rejected,
      message: (state, action) => {
        return getErrors(action, "Could not save team information");
      },
      timeout: longDelay,
    },
    fulfilled: {
      ...defaultNotificationSettings.fulfilled,
      message: () => {
        return "Your details have been saved.";
      },
    },
  });

export const updateInProgressPlayerThunkResponse = (builder) =>
  thunkResponse(builder, updateInProgressPlayerThunk, () => {}, {
    pending: {
      ...defaultNotificationSettings.pending,
      message: () => "Saving your order",
    },
    fulfilled: {
      ...defaultNotificationSettings.fulfilled,
      message: () => "Order has been saved!",
    },
    rejected: {
      ...defaultNotificationSettings.rejected,
      message: (state, action) => {
        return getErrors(action, "Could not save player");
      },
    },
  });

export const updateInProgressSponsorThunkResponse = (builder) =>
  thunkResponse(builder, updateInProgressSponsorThunk, () => {}, {
    pending: {
      ...defaultNotificationSettings.pending,
      message: () => "Saving your order",
    },
    fulfilled: {
      ...defaultNotificationSettings.fulfilled,
      message: () => "Order has been saved!",
    },
    rejected: {
      ...defaultNotificationSettings.rejected,
      message: (state, action) => {
        return getErrors(action, "Could not save Sponsor");
      },
    },
  });
